import { Box, Button, Chip, IconButton, Typography } from '@mui/material';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { CustomCellRendererProps } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useDeleteNodeAttribute, useGetNode } from '../../hooks/api/use-ontology.hook';
import { AttributeApiModel, AttributeRelationship, WithDomains } from '../../../../types/models/attribute.model';
import { useConfirm } from '../../contexts/confirm.context';
import { InfniteScrollingGrid } from '../../components/AgGrid/InfiniteScrollingGrid';
import { NodeType } from '../../../../types/constants/node-type';
import { useUser } from '../../contexts/user.context';
import { ArrayFilter } from '../AgGrid/ArrayFilter';
import { AttributeEntryType } from '../../../../types/constants/attribute-entry-type';
import { NodeApiModel, NodeRelationship, WithParent } from '../../../../types/models/node.model';
import { Permission } from '../../../../types/constants/permission';
import { searchNodeAttributes } from '../../api/ontology.api';
import { AttributeType } from '../../../../types/constants/attribute-type';

export const Attributes = () => {
  const navigate = useNavigate();
  const { nodeType, nodeId } = useParams<{ nodeType: NodeType; nodeId: string; parentNodeId: string }>();
  const [showConfirm] = useConfirm();
  const [user] = useUser();

  const { node } = useGetNode<NodeApiModel & WithParent>({
    nodeId: Number(nodeId),
    nodeType: nodeType as NodeType,
    include: [NodeRelationship.PARENT],
  });
  const [deleteAttribute, { loading: deletingAttribute, error: deleteError }] = useDeleteNodeAttribute();

  if (!nodeType || !nodeId || !node) {
    return null;
  }

  const columns: ColDef<AttributeApiModel & WithDomains>[] = [
    { field: 'name', filter: true, sortable: true, filterParams: { filterOptions: ['contains'], maxNumConditions: 1 } },
    {
      field: 'description',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'type',
      maxWidth: 100,
      filter: ArrayFilter,
      sortable: true,
      filterParams: { values: Object.values(AttributeType) },
    },
    {
      field: 'minimumValue',
      headerName: 'Minimum',
      maxWidth: 100,
      filter: true,
      sortable: true,
      // Filter set to equals as this is a string in the database
      filterParams: { filterOptions: ['equals'], maxNumConditions: 1 },
    },
    {
      field: 'maximumValue',
      headerName: 'Maximum',
      maxWidth: 100,
      filter: true,
      sortable: true,
      // Filter set to equals as this is a string in the database
      filterParams: { filterOptions: ['equals'], maxNumConditions: 1 },
    },
    {
      field: 'numberOfDecimals',
      filter: 'agNumberColumnFilter',
      sortable: true,
      filterParams: { filterOptions: ['inRange'], maxNumConditions: 1 },
    },
    {
      field: 'defaultValue',
      headerName: 'Default',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'values',
      wrapText: true,
      autoHeight: true,
      valueGetter: (params) => params.data?.values?.join(', '),
      cellStyle: { lineHeight: `20px`, paddingTop: '10px' },
    },
    {
      field: 'entryType',
      colId: 'attributeType.type',
      filter: ArrayFilter,
      sortable: true,
      filterParams: { values: Object.values(AttributeEntryType) },
    },
    {
      field: 'domains',
      cellRenderer: ({ data }: CustomCellRendererProps<AttributeApiModel & WithDomains>) =>
        data?.domains.map(({ id, shortName }) => (
          <Chip
            key={id}
            label={shortName}
            color={user?.domains.find((domain) => domain.id === id) ? 'primary' : 'error'}
            variant="outlined"
            size="small"
          />
        )),
    },
  ];

  const actionColumn: ColDef<AttributeApiModel & WithDomains> = {
    headerName: 'Actions',
    minWidth: 120,
    maxWidth: 200,
    cellRenderer: ({ api, node: row }: CustomCellRendererProps<AttributeApiModel>) => (
      <>
        <IconButton onClick={() => navigate(`attributes/edit/${row.id}`)}>
          <EditIcon />
        </IconButton>
        <IconButton
          onClick={() => {
            showConfirm({
              onConfirm: async () => {
                if (row.id) {
                  await deleteAttribute({ nodeId: +nodeId, attributeId: +row.id });
                  api.refreshInfiniteCache();
                }
              },
              title: 'Delete Attribute',
              message: `Are you sure you want to delete attribute "${row.data?.name}"?`,
              error: deleteError?.response?.data?.message,
            });
          }}
        >
          <DeleteIcon />
        </IconButton>
      </>
    ),
  };

  const getAttributes: Parameters<typeof InfniteScrollingGrid<AttributeApiModel>>[0]['onGetRows'] = async ({
    cursor,
    limit,
    filterQuery,
    sortQuery,
  }) => {
    const { data } = await searchNodeAttributes({
      nodeId: +nodeId,
      nodeType,
      ...filterQuery,
      numberOfDecimalsMin: filterQuery?.numberOfDecimals?.min,
      numberOfDecimalsMax: filterQuery?.numberOfDecimals?.max,
      sort: sortQuery,
      include: [AttributeRelationship.DOMAINS],
      cursor,
      limit,
    });
    return data;
  };

  return (
    <Box>
      <Outlet />
      <Box display="flex" flexDirection="column" sx={{ height: '400px', p: '0 12px' }}>
        <Box display="flex" sx={{ m: '10px 0' }}>
          <Typography variant="h6">Attributes</Typography>
          <Box flexGrow={1} />
          {
            // Only survey codes can have attributes at the moment
            nodeType === NodeType.MASTER ? (
              <Button
                color="primary"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate(`attributes/add`)}
              >
                Add Attribute
              </Button>
            ) : null
          }
        </Box>
        <Box alignSelf={'stretch'} flexGrow={1} sx={{ pb: '12px' }}>
          <InfniteScrollingGrid
            loading={deletingAttribute}
            rowHeight={100}
            columnDefs={user?.hasPermission(Permission.ONTOLOGY) ? [...columns, actionColumn] : columns}
            onGetRows={getAttributes}
            enableCellTextSelection={true}
          />
        </Box>
      </Box>
    </Box>
  );
};
